import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { Button } from "@/components/ui/button";
import {
  Bold, Italic, List, ListOrdered, Heading2, Heading3,
  Quote, Code, Link as LinkIcon, Image as ImageIcon, Undo, Redo, Minus
} from "lucide-react";

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt("Enter URL:");
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  const addImage = () => {
    const url = window.prompt("Enter image URL:");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const buttons = [
    { icon: Bold, action: () => editor.chain().focus().toggleBold().run(), active: editor.isActive("bold"), label: "Bold" },
    { icon: Italic, action: () => editor.chain().focus().toggleItalic().run(), active: editor.isActive("italic"), label: "Italic" },
    { type: "sep" },
    { icon: Heading2, action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(), active: editor.isActive("heading", { level: 2 }), label: "H2" },
    { icon: Heading3, action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(), active: editor.isActive("heading", { level: 3 }), label: "H3" },
    { type: "sep" },
    { icon: List, action: () => editor.chain().focus().toggleBulletList().run(), active: editor.isActive("bulletList"), label: "Bullet List" },
    { icon: ListOrdered, action: () => editor.chain().focus().toggleOrderedList().run(), active: editor.isActive("orderedList"), label: "Ordered List" },
    { icon: Quote, action: () => editor.chain().focus().toggleBlockquote().run(), active: editor.isActive("blockquote"), label: "Quote" },
    { icon: Code, action: () => editor.chain().focus().toggleCodeBlock().run(), active: editor.isActive("codeBlock"), label: "Code" },
    { icon: Minus, action: () => editor.chain().focus().setHorizontalRule().run(), label: "Divider" },
    { type: "sep" },
    { icon: LinkIcon, action: addLink, active: editor.isActive("link"), label: "Link" },
    { icon: ImageIcon, action: addImage, label: "Image" },
    { type: "sep" },
    { icon: Undo, action: () => editor.chain().focus().undo().run(), label: "Undo" },
    { icon: Redo, action: () => editor.chain().focus().redo().run(), label: "Redo" },
  ];

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 p-2 bg-slate-50 rounded-t-lg" data-testid="tiptap-toolbar">
      {buttons.map((btn, i) =>
        btn.type === "sep" ? (
          <div key={i} className="w-px h-6 bg-slate-200 mx-1" />
        ) : (
          <Button
            key={i}
            type="button"
            variant="ghost"
            size="icon"
            className={`h-8 w-8 ${btn.active ? "bg-[#00A4A9]/10 text-[#00A4A9]" : "text-slate-500 hover:text-slate-700"}`}
            onClick={btn.action}
            title={btn.label}
          >
            <btn.icon className="w-4 h-4" />
          </Button>
        )
      )}
    </div>
  );
};

export default function TipTapEditor({ content, onChange, placeholder = "Write your content..." }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      Image,
      Placeholder.configure({ placeholder }),
    ],
    content: content || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="tiptap-editor">
      <MenuBar editor={editor} />
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none p-4 min-h-[250px] focus:outline-none [&_.ProseMirror]:min-h-[230px] [&_.ProseMirror]:outline-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-slate-400 [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0"
      />
    </div>
  );
}
